import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import API_URL, { isProdEnv } from '../ServiceModule/Service';
import { logout, showDialog } from '../Features/Auth/AuthSlice';
import {
  getCookie,
  convertToUrlEncoded,
  handleApiResponse,
  prepareEncryptedPayload,
} from '../Utils/networkUtils';
import { removeAllCookies, removeAllSession } from '../LoginModule/LoginUtility';

const baseQuery = fetchBaseQuery({
  baseUrl: API_URL,
  prepareHeaders: (headers) => {
    const csrfToken = getCookie('XSRF-TOKEN');
    if (csrfToken) {
      if (!headers.has('x-csrf-token')) {
        headers.set('x-csrf-token', csrfToken);
      }
      if (!headers.has('X-CSRF-Token')) {
        headers.set('X-CSRF-Token', csrfToken);
      }
    }
    return headers;
  },
  credentials: 'include',
});

let refreshPromise = null;
let isRedirecting = false;

const baseQueryWithReauth = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);

  const isRefreshCall =
    typeof args === 'string'
      ? args.includes('refreshToken')
      : Boolean(args?.url && args.url.includes('refreshToken'));

  if (result.error && result.error.status === 401 && !isRefreshCall) {
    // Prevent multiple concurrent refresh requests
    if (!refreshPromise) {
      refreshPromise = baseQuery({ url: '/users/refreshToken', method: 'POST' }, api, extraOptions);
    }

    const refreshResult = await refreshPromise;

    // Reset the promise deferred to the next macro-task
    setTimeout(() => {
      refreshPromise = null;
    }, 0);

    if (refreshResult.data) {
      // Refresh succeeded — retry the original request
      result = await baseQuery(args, api, extraOptions);
    } else {
      // Refresh failed — session is fully expired or user is unauthenticated
      if (!isRedirecting) {
        isRedirecting = true;
        const state = api.getState();
        const currentEventIdentifier =
          state.currentEvent?.eventName || state.currentEvent?.eventId || '';

        // Clear Redux auth state
        api.dispatch(logout());

        // Wipe localStorage and cookies
        removeAllCookies();

        // Wipe sessionStorage
        removeAllSession();

        api.dispatch(showDialog(true));
        // Hard redirect so all stale component state is reset
        if (typeof window !== 'undefined') {
          const loginPath = currentEventIdentifier
            ? `/events/${currentEventIdentifier}/login`
            : '/';
          window.location.href = loginPath;
        }
      }
    }
  }

  // Handle 403 CSRF errors
  if (result.error && result.error.status === 403) {
    const message = result.error.data?.message || '';
    if (message.toLowerCase().includes('csrf')) {
      // Fetch a fresh CSRF token
      const refreshResult = await baseQuery(
        { url: '/system/csrf-token', method: 'GET' },
        api,
        extraOptions
      );

      if (refreshResult.data && refreshResult.data.csrfToken) {
        // Retry the initial query with the new token from either cookie or response body fallback
        const newToken = getCookie('XSRF-TOKEN') || refreshResult.data.csrfToken;

        // Clone args and headers for the retry
        const retryArgs = typeof args === 'string' ? { url: args } : { ...args };
        retryArgs.headers = {
          ...retryArgs.headers,
          'x-csrf-token': newToken,
        };

        result = await baseQuery(retryArgs, api, extraOptions);
      }
    }
  }

  // Centralized response handling for errors and status flags
  handleApiResponse(result);

  return result;
};

// Helper for common mutation headers
const jsonHeaders = {
  Accept: 'application/json',
  'Content-Type': 'application/json',
};

const multipartHeaders = {
  Accept: '*/*',
  'Content-Type': 'multipart/form-data',
};

const urlEncodedHeaders = {
  'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
};

export const api = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Post', 'Cart', 'cartData', 'notification'],
  endpoints: (builder) => ({
    // Standard POST with URL encoding
    postAPI: builder.mutation({
      query: ({ url, formData }) => ({
        url,
        method: 'POST',
        headers: urlEncodedHeaders,
        body: convertToUrlEncoded(formData),
      }),
      invalidatesTags: ['Post', 'Cart'],
    }),

    // Multipart POST
    postApiMultiPartData: builder.mutation({
      query: ({ url, formData }) => ({
        url,
        method: 'POST',
        headers: multipartHeaders,
        body: formData,
      }),
      invalidatesTags: ['Post', 'Cart'],
    }),

    // Login POST
    postLoginAPI: builder.mutation({
      query: ({ url, formData }) => ({
        url,
        method: 'POST',
        headers: urlEncodedHeaders,
        body: { userPhone: formData },
      }),
    }),

    // JSON POST (With Token pattern)
    postAPIWithToken: builder.mutation({
      query: ({ url, formData }) => ({
        url,
        method: 'POST',
        headers: jsonHeaders,
        body: formData,
      }),
      invalidatesTags: ['Post', 'Cart'],
    }),

    // JSON POST (Without Token pattern)
    postAPIWithOutToken: builder.mutation({
      query: ({ url, formData }) => ({
        url,
        method: 'POST',
        headers: jsonHeaders,
        body: formData,
      }),
    }),

    // Notification POSTs
    postNotificationAPIWithOutToken: builder.mutation({
      query: ({ url, formData }) => ({
        url,
        method: 'POST',
        headers: jsonHeaders,
        body: formData,
      }),
      invalidatesTags: ['notification'],
    }),
    postNotificationAPIWithToken: builder.mutation({
      query: ({ url, formData }) => ({
        url,
        method: 'POST',
        headers: jsonHeaders,
        body: formData,
      }),
      invalidatesTags: ['notification'],
    }),

    // Cart and Order POSTs
    orderpostAPIWithToken: builder.mutation({
      query: ({ url, formData }) => ({
        url,
        method: 'POST',
        headers: jsonHeaders,
        body: formData,
      }),
      invalidatesTags: ['cartData'],
    }),

    // Encrypted Payload POSTs
    postAddToCartWithToken: builder.mutation({
      async queryFn({ url, formData }, _queryApi, _extraOptions, baseQuery) {
        try {
          const bodyPayload = await prepareEncryptedPayload(formData);
          const result = await baseQuery({
            url,
            method: 'POST',
            headers: jsonHeaders,
            body: bodyPayload,
          });
          return result.error ? { error: result.error } : { data: result.data };
        } catch (error) {
          return { error };
        }
      },
      invalidatesTags: ['cartData'],
    }),
    removeFromCartWithToken: builder.mutation({
      async queryFn({ url, formData }, _queryApi, _extraOptions, baseQuery) {
        try {
          const bodyPayload = await prepareEncryptedPayload(formData);
          const result = await baseQuery({
            url,
            method: 'POST',
            headers: jsonHeaders,
            body: bodyPayload,
          });
          return result.error ? { error: result.error } : { data: result.data };
        } catch (error) {
          return { error };
        }
      },
      invalidatesTags: ['cartData'],
    }),

    // Queries
    getAPI: builder.query({
      query: ({ url }) => ({ url, method: 'GET', headers: jsonHeaders }),
      providesTags: ['Post', 'Cart'],
    }),
    getNotificationAPI: builder.query({
      query: ({ url }) => ({ url, method: 'GET', headers: jsonHeaders }),
      providesTags: ['notification'],
    }),
    getCartDataAPI: builder.query({
      query: ({ url }) => ({ url, method: 'GET', headers: jsonHeaders }),
      providesTags: ['cartData'],
      keepUnusedDataFor: 0,
    }),
    calculateGuestCart: builder.query({
      query: ({ url, formData }) => ({
        url,
        method: 'POST',
        headers: jsonHeaders,
        body: formData,
      }),
      providesTags: ['Cart'],
      keepUnusedDataFor: 0,
    }),
    getAPIWithoutToken: builder.query({
      query: (url) => ({ url, method: 'GET', headers: jsonHeaders }),
      providesTags: ['Post', 'Cart'],
    }),

    // Deletion
    deleteAPI: builder.mutation({
      query: ({ url, _id }) => ({
        url,
        method: 'POST',
        headers: jsonHeaders,
        body: { _id },
      }),
      invalidatesTags: ['Post', 'Cart'],
    }),

    // File Upload
    uploadAPI: builder.mutation({
      query: ({ url, formData }) => ({
        url,
        method: 'POST',
        headers: { Accept: '*/*' }, // Browser handles Content-Type for FormData
        body: formData,
      }),
      invalidatesTags: ['Post', 'Cart'],
    }),
  }),
});

export const {
  usePostAPIMutation,
  usePostLoginAPIMutation,
  usePostAPIWithTokenMutation,
  usePostAPIWithOutTokenMutation,
  useGetAPIQuery,
  useLazyGetAPIQuery,
  useGetAPIWithoutTokenQuery,
  useDeleteAPIMutation,
  useUploadAPIMutation,
  usePostApiMultiPartDataMutation,
  usePostAddToCartWithTokenMutation,
  useRemoveFromCartWithTokenMutation,
  useGetCartDataAPIQuery,
  useOrderpostAPIWithTokenMutation,
  usePostNotificationAPIWithOutTokenMutation,
  usePostNotificationAPIWithTokenMutation,
  useGetNotificationAPIQuery,
  useCalculateGuestCartQuery,
} = api;

export const convertBody = convertToUrlEncoded;
